import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MyPageBanner from "../components/MyPageBanner";
import AuthPanel from "../components/AuthPanel";
import AccountPanel, { ChangePasswordForm } from "../components/AccountPanel";
import TeamRegistrationForm from "../components/TeamRegistrationForm";
import MyTeamEditor from "../components/MyTeamEditor";
import TeamLogo from "../components/TeamLogo";
import StateMessage from "../components/StateMessage";
import { pickHighlightGame } from "../components/TeamHighlightGame";
import { formatDayLabel, formatGameTime } from "../Functions/Formatting";
import { matchStatus } from "../Functions/matchStatus";
import { useAuth } from "../auth";
import { supabase } from "../supabaseClient";
import {
  formatPreferredDay,
  normalizePreferredDays,
} from "../preferredDays";
import { getDrafts, deleteDraft } from "../utils/drafts";
import "./RegisterTeamPage.css";
import "./MyTeamsPage.css";

const TABS = [
  { id: "equipos", label: "Equipos" },
  { id: "solicitudes", label: "Solicitudes" },
  { id: "cuenta", label: "Cuenta" },
];

const MAX_DRAFTS = 3;

function formatStatus(status) {
  if (status === "pending") return "En revisión";
  if (status === "approved") return "Aceptada";
  if (status === "rejected") return "No aceptada";
  return status;
}

/** Drafts store the wizard state as { form: {...}, ... }. */
function draftTeamName(draft) {
  return String(draft?.data?.form?.team_name || draft?.data?.team_name || "").trim();
}

function formatDraftSaved(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function initials(name, email) {
  const source = String(name || email || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/** One-line summary of a team's next (or last) game. */
function GameSummary({ highlight, teamId }) {
  if (!highlight) {
    return <p className="owner-team-game is-empty">Sin partidos programados todavía.</p>;
  }
  const { game, kind } = highlight;
  const isTeam1 = String(game.team1_id) === String(teamId);
  const opponent = (isTeam1 ? game.team2_name : game.team1_name) || "Por definir";
  const status = matchStatus(game);

  let result = null;
  if (status === "played") {
    const mine = Number(isTeam1 ? game.team1_score : game.team2_score) || 0;
    const theirs = Number(isTeam1 ? game.team2_score : game.team1_score) || 0;
    const outcome = mine > theirs ? "win" : mine < theirs ? "loss" : "draw";
    result = (
      <span className={`owner-result owner-result--${outcome}`}>
        {outcome === "win" ? "G" : outcome === "loss" ? "P" : "E"} {mine}-{theirs}
      </span>
    );
  }

  return (
    <p className="owner-team-game">
      <span className="owner-team-game-label">
        {kind === "previous" ? "Último partido" : "Próximo partido"}
      </span>
      <span className="owner-team-game-detail">
        {result}
        <span>vs {opponent}</span>
        <span className="owner-team-game-when">
          {status === "cancelled"
            ? "Cancelado"
            : `${formatDayLabel(game.game_date)}${
                kind === "next" && game.game_time ? `, ${formatGameTime(game.game_time)}` : ""
              }`}
        </span>
      </span>
    </p>
  );
}

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const MyTeamsPage = ({ bannerImage }) => {
  const { user, profile, loading: authLoading, recoveryMode, clearRecoveryMode } =
    useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const tab = TABS.some((t) => t.id === requestedTab) ? requestedTab : "equipos";
  const selectTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "equipos") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };
  const onTabKeyDown = (event) => {
    const idx = TABS.findIndex((t) => t.id === tab);
    let nextIdx = null;
    if (event.key === "ArrowRight") nextIdx = (idx + 1) % TABS.length;
    if (event.key === "ArrowLeft") nextIdx = (idx - 1 + TABS.length) % TABS.length;
    if (nextIdx === null) return;
    event.preventDefault();
    selectTab(TABS[nextIdx].id);
    document.getElementById(`owner-tab-${TABS[nextIdx].id}`)?.focus();
  };

  // Depend on the id, not the user object: token refreshes hand out a new
  // user object every hour, which used to reload (and reset) everything.
  const userId = user?.id || null;

  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamsErr, setTeamsErr] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamGames, setTeamGames] = useState({});

  const [myRegs, setMyRegs] = useState([]);
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [regsErr, setRegsErr] = useState(false);

  const [leagues, setLeagues] = useState([]);
  const [loadingLeagues, setLoadingLeagues] = useState(true);
  const [leaguesErr, setLeaguesErr] = useState(null);

  const [showRegister, setShowRegister] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  
  // Drafts state
  const [drafts, setDrafts] = useState([]);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [draftLimitHit, setDraftLimitHit] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const lastFocusRef = useRef(null);
  const [recoveryDone, setRecoveryDone] = useState(false);

  const openRegistration = (event) => {
    if (event?.currentTarget) lastFocusRef.current = event.currentTarget;
    if (userId) {
      const existingDrafts = getDrafts(userId);
      setDrafts(existingDrafts);
      if (existingDrafts.length >= MAX_DRAFTS) {
        setDraftLimitHit(true);
        return;
      }
    }
    setDraftLimitHit(false);
    setSelectedDraftId(null);
    setConfirmingClose(false);
    setShowRegister(true);
    setRegSuccess(false);
  };

  const resumeRegistration = (draftId, event) => {
    if (event?.currentTarget) lastFocusRef.current = event.currentTarget;
    setSelectedDraftId(draftId);
    setConfirmingClose(false);
    setShowRegister(true);
    setRegSuccess(false);
  };

  // Closing asks first (inline, inside the sheet): progress autosaves except the logo.
  const handleCloseOverlay = () => {
    if (showRegister) setConfirmingClose(true);
  };

  const closeRegistration = () => {
    if (userId) setDrafts(getDrafts(userId));
    setConfirmingClose(false);
    setShowRegister(false);
    lastFocusRef.current?.focus?.();
  };

  const handleDeleteDraft = (draftId) => {
    deleteDraft(userId, draftId);
    setDrafts(getDrafts(userId));
    setConfirmDeleteId(null);
    setDraftLimitHit(false);
  };

  useEffect(() => {
    if (!showRegister) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setConfirmingClose(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showRegister]);

  useEffect(() => {
    if (userId) {
      setDrafts(getDrafts(userId));
    }
  }, [userId]);

  useEffect(() => {
    if (showRegister) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showRegister]);

  useEffect(() => {
    if (showRegister) {
      document.body.classList.add("overlay-open");
    } else {
      document.body.classList.remove("overlay-open");
    }
    return () => {
      document.body.classList.remove("overlay-open");
    };
  }, [showRegister]);

  const loadTeams = useCallback(async () => {
    if (!userId) {
      setTeams([]);
      return;
    }
    setLoadingTeams(true);
    setTeamsErr(false);
    try {
      // Primary: teams linked by owner_user_id
      const { data: owned, error: ownedErr } = await supabase
        .from("teams")
        .select(
          `
          id, team_name, logo_url, league_id,
          primary_kit_colors, secondary_kit_colors,
          team_contacts ( owner_name, phone, email ),
          leagues ( id, league_name, season, is_published, theme_color, archived )
        `
        )
        .eq("owner_user_id", userId)
        .order("team_name");
      if (ownedErr) throw ownedErr;

      // Fallback: approved registrations (covers teams approved before owner_user_id was set)
      const { data: fromRegs, error: regsErr } = await supabase
        .from("team_registrations")
        .select(
          `
          approved_team_id,
          teams (
            id, team_name, logo_url, league_id, owner_user_id,
            primary_kit_colors, secondary_kit_colors,
            team_contacts ( owner_name, phone, email ),
            leagues ( id, league_name, season, is_published, theme_color, archived )
          )
        `
        )
        .eq("user_id", userId)
        .eq("status", "approved")
        .not("approved_team_id", "is", null);
      if (regsErr) throw regsErr;

      const flattenContact = (t) => ({
        ...t,
        owner_name: t.team_contacts?.owner_name ?? null,
        phone: t.team_contacts?.phone ?? null,
        email: t.team_contacts?.email ?? null,
      });
      const byId = new Map();
      (owned || []).forEach((t) => byId.set(t.id, flattenContact(t)));
      (fromRegs || []).forEach((r) => {
        const t = r.teams;
        if (t?.id && !byId.has(t.id)) byId.set(t.id, flattenContact(t));
      });

      const merged = Array.from(byId.values()).sort((a, b) =>
        String(a.team_name || "").localeCompare(String(b.team_name || ""))
      );
      setTeams(merged);
    } catch (e) {
      console.error(e);
      // Keep whatever was already shown; report the failure instead of "no teams".
      setTeamsErr(true);
    } finally {
      setLoadingTeams(false);
    }
  }, [userId]);

  const loadMyRegs = useCallback(async () => {
    if (!userId) {
      setMyRegs([]);
      return;
    }
    setLoadingRegs(true);
    setRegsErr(false);
    try {
      const { data, error } = await supabase
        .from("team_registrations")
        .select(
          `
          id, team_name, status, created_at, league_id, preferred_days,
          leagues ( league_name, season )
        `
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setMyRegs(data || []);
    } catch (e) {
      console.error(e);
      setRegsErr(true);
    } finally {
      setLoadingRegs(false);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from("leagues")
          .select("id, league_name, season, active_days")
          .eq("looking_for_teams", true)
          .eq("is_published", true)
          .order("league_name");
        if (error) throw error;
        if (!cancelled) setLeagues(data || []);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setLeaguesErr("No se pudieron cargar las ligas abiertas.");
        }
      } finally {
        if (!cancelled) setLoadingLeagues(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadTeams();
    loadMyRegs();
  }, [loadTeams, loadMyRegs]);

  useEffect(() => {
    if (!teams.length) {
      setTeamGames({});
      return;
    }

    let cancelled = false;
    const teamIds = teams.map((t) => t.id);

    const loadGames = async () => {
      try {
        const { data, error } = await supabase
          .from("games")
          .select(
            `
            game_id:id,
            league_id,
            team1_id,
            team2_id,
            team1_score,
            team2_score,
            game_date,
            game_time,
            status,
            t1:teams!team1_id(team_name, logo_url, primary_kit_colors),
            t2:teams!team2_id(team_name, logo_url, primary_kit_colors),
            leagues!inner(id)
          `
          )
          .eq("is_published", true)
          .eq("leagues.is_published", true)
          .or(
            `team1_id.in.(${teamIds.join(",")}),team2_id.in.(${teamIds.join(",")})`
          )
          .order("game_date", { ascending: true })
          .order("game_time", { ascending: true });

        if (error) throw error;

        const byTeam = {};
        teamIds.forEach((id) => {
          byTeam[id] = [];
        });

        (data || []).forEach((row) => {
          const mapped = {
            ...row,
            team1_name: row.t1?.team_name,
            team2_name: row.t2?.team_name,
            team1_logo_url: row.t1?.logo_url || null,
            team2_logo_url: row.t2?.logo_url || null,
            team1_kit_colors: row.t1?.primary_kit_colors || [],
            team2_kit_colors: row.t2?.primary_kit_colors || [],
          };
          if (byTeam[row.team1_id]) byTeam[row.team1_id].push(mapped);
          if (byTeam[row.team2_id] && row.team2_id !== row.team1_id) {
            byTeam[row.team2_id].push(mapped);
          }
        });

        if (!cancelled) setTeamGames(byTeam);
      } catch (e) {
        console.error(e);
        if (!cancelled) setTeamGames({});
      }
    };

    loadGames();
    return () => {
      cancelled = true;
    };
  }, [teams]);

  const highlightsByTeam = useMemo(() => {
    const map = {};
    teams.forEach((t) => {
      map[t.id] = pickHighlightGame(teamGames[t.id] || []);
    });
    return map;
  }, [teams, teamGames]);

  const { activeTeams, archivedTeams } = useMemo(() => {
    const active = [];
    const archived = [];
    teams.forEach((t) => {
      if (t.leagues?.archived) archived.push(t);
      else active.push(t);
    });
    return { activeTeams: active, archivedTeams: archived };
  }, [teams]);

  const selectedTeam = teams.find((t) => t.id === selectedTeamId) || null;
  const pendingRegs = myRegs.filter((r) => r.status === "pending");
  const canRegister = !loadingLeagues && !leaguesErr && leagues.length > 0;

  const renderTeamCard = (t, { showHighlight = true } = {}) => {
    const open = selectedTeamId === t.id;
    const league = t.leagues;
    const editorId = `team-editor-${t.id}`;
    return (
      <li
        key={t.id}
        className={`owner-team${open ? " is-open" : ""}`}
        style={{ "--team-accent": league?.theme_color || "var(--accent)" }}
      >
        <div className="owner-team-head">
          <TeamLogo name={t.team_name} logoUrl={t.logo_url} size="lg" />
          <div className="owner-team-copy">
            <h3 className="owner-team-name">{t.team_name}</h3>
            <p className="owner-team-league">
              {league?.league_name || "Liga"}
              {league?.season ? `, temporada ${league.season}` : ""}
            </p>
          </div>
        </div>

        {showHighlight && (
          <GameSummary highlight={highlightsByTeam[t.id]} teamId={t.id} />
        )}

        <div className="owner-team-actions">
          <button
            type="button"
            className={`owner-action${open ? " is-active" : ""}`}
            aria-expanded={open}
            aria-controls={editorId}
            onClick={() => setSelectedTeamId((id) => (id === t.id ? null : t.id))}
          >
            {open ? "Cerrar edición" : "Editar equipo"}
          </button>
          {league?.is_published && (
            <Link to={`/ligas/${t.league_id}`} className="owner-action owner-action--link">
              Ver liga
            </Link>
          )}
        </div>

        {open && selectedTeam && (
          <div id={editorId}>
            <MyTeamEditor
              team={selectedTeam}
              onClose={() => setSelectedTeamId(null)}
              onSaved={loadTeams}
            />
          </div>
        )}
      </li>
    );
  };

  const displayName = profile?.full_name || user?.user_metadata?.full_name || "";
  const firstName = displayName.split(/\s+/)[0];

  const teamsPanel = (
    <>
      {teamsErr && (
        <div className="ui-state ui-state--error" role="alert">
          <p className="ui-state-title">No se pudieron cargar tus equipos.</p>
          <button type="button" className="ui-state-link acct-link" onClick={loadTeams}>
            Intentar de nuevo
          </button>
        </div>
      )}
      {loadingTeams && teams.length === 0 ? (
        <div className="owner-skeletons" role="status" aria-label="Cargando equipos…">
          <span className="skeleton owner-skeleton" />
          <span className="skeleton owner-skeleton" />
        </div>
      ) : teamsErr && teams.length === 0 ? null : activeTeams.length === 0 ? (
        <div className="ui-state">
          <p className="ui-state-title">
            {teams.length === 0 ? "Aún no tienes equipos" : "Sin equipos en ligas activas"}
          </p>
          <p>
            Cuando la liga acepte tu solicitud, tu equipo aparecerá aquí para que
            lo administres.
          </p>
          <button
            type="button"
            className="ui-state-link acct-link"
            onClick={() => selectTab("solicitudes")}
          >
            Registrar un equipo →
          </button>
        </div>
      ) : (
        <ul className="owner-team-list">
          {activeTeams.map((t) => renderTeamCard(t))}
        </ul>
      )}

      {!loadingTeams && archivedTeams.length > 0 && (
        <details className="owner-archive">
          <summary>
            Temporadas anteriores
            <span className="owner-count">{archivedTeams.length}</span>
          </summary>
          <ul className="owner-team-list">
            {archivedTeams.map((t) => renderTeamCard(t, { showHighlight: false }))}
          </ul>
        </details>
      )}
    </>
  );

  const requestsPanel = (
    <>
      <section className="owner-register" aria-labelledby="owner-register-title">
        <div className="owner-register-copy">
          <h2 className="owner-section-title" id="owner-register-title">
            Registrar un equipo
          </h2>
          <p className="owner-section-desc">
            Envía una solicitud y la administración de la liga te contactará.
          </p>
        </div>
        {loadingLeagues ? (
          <span className="skeleton owner-cta-skeleton" />
        ) : leaguesErr ? (
          <p className="reg-status error" role="alert">{leaguesErr}</p>
        ) : canRegister ? (
          <button type="button" className="reg-cta" onClick={openRegistration}>
            Nueva solicitud
          </button>
        ) : (
          <p className="owner-closed">Por ahora ninguna liga está buscando equipos.</p>
        )}
      </section>

      {draftLimitHit && (
        <div className="owner-notice" role="alert">
          Tienes {MAX_DRAFTS} borradores guardados, el máximo. Continúa o elimina uno
          para empezar otra solicitud.
        </div>
      )}

      {regSuccess && (
        <div className="reg-success" role="status">
          <strong>Solicitud enviada.</strong> La revisaremos pronto y es posible que
          te escribamos para confirmar detalles.
        </div>
      )}

      {drafts.length > 0 && (
        <section className="owner-group" aria-labelledby="owner-drafts-title">
          <h3 className="owner-group-title" id="owner-drafts-title">
            Borradores <span className="owner-count">{drafts.length}</span>
          </h3>
          <ul className="owner-rows">
            {drafts.map((d) => (
              <li key={d.id} className="owner-row">
                <div className="owner-row-main">
                  <span className="owner-row-title">
                    {draftTeamName(d) || "Equipo sin nombre"}
                  </span>
                  <span className="owner-row-sub">
                    Guardado {formatDraftSaved(d.updatedAt)}
                  </span>
                </div>
                {confirmDeleteId === d.id ? (
                  <div className="owner-row-actions" role="group" aria-label="Confirmar eliminación">
                    <span className="owner-confirm-text">¿Eliminar?</span>
                    <button type="button" className="owner-action" onClick={() => setConfirmDeleteId(null)}>
                      No
                    </button>
                    <button type="button" className="owner-action owner-action--danger" onClick={() => handleDeleteDraft(d.id)}>
                      Sí, eliminar
                    </button>
                  </div>
                ) : (
                  <div className="owner-row-actions">
                    <button
                      type="button"
                      className="owner-action owner-action--quiet"
                      onClick={() => setConfirmDeleteId(d.id)}
                      aria-label={`Eliminar borrador ${draftTeamName(d)}`.trim()}
                    >
                      Eliminar
                    </button>
                    <button
                      type="button"
                      className="owner-action owner-action--primary"
                      onClick={(e) => resumeRegistration(d.id, e)}
                      disabled={!canRegister}
                    >
                      Continuar
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="owner-group" aria-labelledby="owner-sent-title">
        <h3 className="owner-group-title" id="owner-sent-title">
          Enviadas <span className="owner-count">{myRegs.length}</span>
        </h3>
        {loadingRegs && myRegs.length === 0 ? (
          <div className="owner-skeletons" role="status" aria-label="Cargando solicitudes…">
            <span className="skeleton owner-row-skeleton" />
          </div>
        ) : regsErr && myRegs.length === 0 ? (
          <div className="ui-state ui-state--error" role="alert">
            <p className="ui-state-title">No se pudieron cargar tus solicitudes.</p>
            <button type="button" className="ui-state-link acct-link" onClick={loadMyRegs}>
              Intentar de nuevo
            </button>
          </div>
        ) : myRegs.length === 0 ? (
          <StateMessage plain>Aún no has enviado solicitudes.</StateMessage>
        ) : (
          <ul className="owner-rows">
            {myRegs.map((r) => {
              const days = normalizePreferredDays(r.preferred_days);
              return (
                <li key={r.id} className="owner-row">
                  <div className="owner-row-main">
                    <span className="owner-row-title">{r.team_name}</span>
                    <span className="owner-row-sub">
                      {r.leagues?.league_name || "Liga"}
                      {r.leagues?.season ? `, ${r.leagues.season}` : ""}
                    </span>
                    {days.length > 0 && (
                      <span className="owner-row-sub">
                        {days.map(formatPreferredDay).join(", ")}
                      </span>
                    )}
                  </div>
                  <div className="owner-row-meta">
                    <span className={`reg-mine-status status-${r.status}`}>
                      {formatStatus(r.status)}
                    </span>
                    <span className="owner-row-date">{formatWhen(r.created_at)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {pendingRegs.length >= 5 && (
          <p className="reg-hint">
            Tienes {pendingRegs.length} solicitudes en revisión, el límite. Espera
            una respuesta antes de enviar otra.
          </p>
        )}
      </section>
    </>
  );

  return (
    <>
      <MyPageBanner imageUrl={bannerImage} text="Mis equipos" />

      <div className="reg-page owner-page">
        {authLoading && (
          <div className="owner-skeletons" role="status" aria-label="Cargando sesión…">
            <span className="skeleton owner-header-skeleton" />
            <span className="skeleton owner-skeleton" />
          </div>
        )}

        {!authLoading && !user && (
          <div className="reg-block acct-auth-card">
            <AuthPanel />
          </div>
        )}

        {!authLoading && user && (
          <div className="owner-shell">
            {recoveryMode && (
              <section className="owner-recovery" aria-labelledby="owner-recovery-title">
                <h2 className="owner-section-title" id="owner-recovery-title">
                  Crea tu nueva contraseña
                </h2>
                {recoveryDone ? (
                  <>
                    <div className="reg-success" role="status">
                      Listo. Tu contraseña nueva ya está activa.
                    </div>
                    <button
                      type="button"
                      className="owner-action owner-action--primary"
                      onClick={() => {
                        setRecoveryDone(false);
                        clearRecoveryMode();
                      }}
                    >
                      Continuar
                    </button>
                  </>
                ) : (
                  <>
                    <p className="owner-section-desc">
                      Entraste con el enlace de recuperación. Elige una contraseña
                      nueva para tu cuenta.
                    </p>
                    <ChangePasswordForm
                      submitLabel="Guardar contraseña"
                      onDone={() => setRecoveryDone(true)}
                    />
                    <button type="button" className="acct-link" onClick={clearRecoveryMode}>
                      Ahora no
                    </button>
                  </>
                )}
              </section>
            )}

            <header className="owner-header">
              <span className="owner-avatar" aria-hidden="true">
                {initials(displayName, user.email)}
              </span>
              <div className="owner-header-copy">
                <p className="owner-hello">
                  {firstName ? `Hola, ${firstName}` : "Tu cuenta"}
                </p>
                <p className="owner-email">{user.email}</p>
              </div>
            </header>

            <div
              className="owner-tabs"
              role="tablist"
              aria-label="Secciones de tu cuenta"
              onKeyDown={onTabKeyDown}
            >
              {TABS.map((t) => {
                const selected = tab === t.id;
                const count =
                  t.id === "equipos"
                    ? activeTeams.length
                    : t.id === "solicitudes"
                      ? pendingRegs.length + drafts.length
                      : 0;
                return (
                  <button
                    key={t.id}
                    id={`owner-tab-${t.id}`}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    aria-controls={`owner-panel-${t.id}`}
                    tabIndex={selected ? 0 : -1}
                    className={`owner-tab${selected ? " active" : ""}`}
                    onClick={() => selectTab(t.id)}
                  >
                    {t.label}
                    {count > 0 && <span className="owner-tab-count">{count}</span>}
                  </button>
                );
              })}
            </div>

            <div
              className="owner-panel"
              role="tabpanel"
              id={`owner-panel-${tab}`}
              aria-labelledby={`owner-tab-${tab}`}
            >
              {tab === "equipos" && teamsPanel}
              {tab === "solicitudes" && requestsPanel}
              {tab === "cuenta" && <AccountPanel />}
            </div>

            {showRegister && canRegister && (
              <div className="reg-overlay" onClick={handleCloseOverlay}>
                <div
                  className="reg-overlay-content"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="reg-overlay-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="reg-overlay-header">
                    <div>
                      <h2 className="reg-overlay-title" id="reg-overlay-title">
                        Nueva solicitud
                      </h2>
                      <p className="reg-overlay-desc">
                        Tu avance se guarda solo como borrador.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="reg-overlay-close"
                      onClick={handleCloseOverlay}
                      aria-label="Cerrar formulario"
                    >
                      &times;
                    </button>
                  </div>

                  {confirmingClose && (
                    <div className="owner-close-confirm" role="alertdialog" aria-label="Salir del formulario">
                      <p>
                        ¿Salir? Tu avance queda guardado como borrador, excepto el logo.
                      </p>
                      <div className="owner-row-actions">
                        <button
                          type="button"
                          className="owner-action"
                          onClick={() => setConfirmingClose(false)}
                          autoFocus
                        >
                          Seguir editando
                        </button>
                        <button
                          type="button"
                          className="owner-action owner-action--primary"
                          onClick={closeRegistration}
                        >
                          Salir
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="reg-overlay-body">
                    <TeamRegistrationForm
                      leagues={leagues}
                      onSuccess={(formDraftId) => {
                        // The form autosaves under its own id (new or resumed draft);
                        // a sent request must not linger as a draft.
                        const sentDraftId = formDraftId || selectedDraftId;
                        if (sentDraftId) deleteDraft(userId, sentDraftId);
                        setShowRegister(false);
                        setConfirmingClose(false);
                        setRegSuccess(true);
                        loadMyRegs();
                        setDrafts(getDrafts(userId));
                      }}
                      onCancel={handleCloseOverlay}
                      draftId={selectedDraftId}
                      initialData={drafts.find((d) => d.id === selectedDraftId)?.data}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default MyTeamsPage;
